import { supabase } from "@/lib/supabase";
import * as Sentry from "@sentry/react";
import { redirect } from "@tanstack/react-router";

const { logger } = Sentry;

const AUTH_USER_REDIRECT = "/workspaces";
const GUEST_REDIRECT = "/login";

/**
 * Guard for routes that are irrelevant once authenticated (e.g. /login).
 * Redirects to /workspaces if a session already exists.
 */
export async function requireGuest(redirectTo: string = AUTH_USER_REDIRECT) {
  let session = null;

  try {
    const result = await supabase.auth.getSession();
    session = result.data.session;
  } catch (error) {
    logger.error("Auth: requireGuest failed to get session");
    Sentry.captureException(error);
    return;
  }

  // `href`, not `to`: CR-288's return-to may carry a query string. Callers
  // only pass app paths (the invite page, or a safeRedirectPath result).
  if (session) throw redirect({ href: redirectTo });
}

/**
 * Guard for routes that require authentication.
 * Redirects to /login if no session exists, otherwise returns the user.
 *
 * Calls getSession() directly so Supabase can proactively refresh an expired
 * access token before any data fetches run in child beforeLoads. In the common
 * case (token still valid) this is just a memory read inside the Supabase
 * client — no network request is made.
 *
 * Same pattern as requireGuest — redirect is thrown outside the try/catch
 * so it always propagates correctly to TanStack Router.
 */
export async function requireAuth(redirectTo = GUEST_REDIRECT) {
  let session = null;

  try {
    const result = await supabase.auth.getSession();
    session = result.data.session;
  } catch (error) {
    logger.error("Auth: requireAuth failed to get session");
    Sentry.captureException(error);
  }

  if (!session) throw redirect({ to: redirectTo });
  return session.user;
}
